import React, { useMemo, useState } from 'react';
import { FiTrash2, FiX, FiUsers, FiMail, FiShield } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import { Avatar } from '../ui/Avatar';
import { extractApiError } from '../../utils/apiError';
import {
  useGetProjectMembers,
  useGetProjectInvitations,
  useCreateProjectInvitation,
  // useResendProjectInvitation,
  useCancelProjectInvitation,
  useUpdateProjectMemberRole,
  useRemoveProjectMember,
  type ProjectRole,
  type ApiProjectMember,
  type ApiProjectInvitation,
  type ProjectPermissions,
} from '../../api/projects';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_PERMISSION_TITLE = "You don't have permission to perform this action.";

const ROLE_LABEL: Record<string, string> = {
  manager: 'Manager',
  editor: 'Editor',
  guest: 'Guest',
};

const ASSIGNABLE_ROLES: ProjectRole[] = ['manager', 'editor', 'guest'];

const REMOVE_PERMISSION_KEY: Record<ProjectRole, keyof ProjectPermissions> = {
  manager: 'can_remove_manager',
  editor: 'can_remove_editor',
  guest: 'can_remove_guest',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const displayName = (u: { full_name: string | null; username: string | null; email: string }) =>
  u.full_name?.trim() || u.username?.trim() || u.email;

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  isDestructive?: boolean;
  onConfirm: () => void;
}

interface ProjectCollaboratorsCardProps {
  projectUuid: string;
  currentUserId?: string | number;
  /** True when the current user is the project's creator (implicit owner, not an assignable role). */
  isOwner: boolean;
  /** Capability flags from the Get Project Details response — the source of truth for what
   * this user may do here. When omitted, falls back to the legacy owner/manager check. */
  permissions?: ProjectPermissions;
  /** When true, disables all invitation and role management capabilities. */
  isArchived?: boolean;
}

export const ProjectCollaboratorsCard: React.FC<ProjectCollaboratorsCardProps> = ({
  projectUuid,
  currentUserId,
  isOwner,
  permissions,
  isArchived = false,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectRole>('editor');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const membersQuery = useGetProjectMembers({ projectUuid, page_size: 100 });
  const invitationsQuery = useGetProjectInvitations({ projectUuid, page_size: 100 });

  const inviteMutation = useCreateProjectInvitation();
  // const resendMutation = useResendProjectInvitation();
  const cancelMutation = useCancelProjectInvitation();
  const updateRoleMutation = useUpdateProjectMemberRole();
  const removeMutation = useRemoveProjectMember();

  const members = membersQuery.data?.results ?? [];
  const memberCount = membersQuery.data?.count ?? members.length;
  const pendingInvitations = useMemo(
    () => (invitationsQuery.data?.results ?? []).filter((inv) => inv.status === 'pending'),
    [invitationsQuery.data],
  );

  const isSelf = (m: ApiProjectMember) => String(m.user.id) === String(currentUserId);
  const selfMember = members.find(isSelf);
  // Legacy fallback for callers that haven't supplied `permissions` yet — once the API's
  // permissions object is present it is the sole source of truth, no role guessing.
  const legacyCanManage = isOwner || selfMember?.role === 'manager';

  const canInviteRole = (r: ProjectRole) => {
    if (isArchived) return false;
    if (isOwner) {
      return true;
    }
    if (!selfMember) return false;

    if (selfMember.role === 'manager') {
      return r === 'editor' || r === 'guest';
    }
    if (selfMember.role === 'editor') {
      return r === 'guest';
    }
    return false;
  };
  const canRemoveRole = (r: ProjectRole) =>
    isArchived ? false : permissions ? permissions[REMOVE_PERMISSION_KEY[r]] : legacyCanManage;
  const canChangeRole = isArchived
    ? false
    : permissions
      ? permissions.can_change_member_role
      : legacyCanManage;

  const invitableRoles = ASSIGNABLE_ROLES.filter(canInviteRole);
  const canInviteAny = invitableRoles.length > 0;
  const effectiveInviteRole = invitableRoles.includes(role) ? role : invitableRoles[0];

  // Plain click handler, not a form submit — this card is nested inside the
  // page's outer <form onSubmit={handleSave}>, and HTML forms cannot nest
  // reliably (the inner submit would bubble up and reload/save the outer form).
  const handleInvite = () => {
    if (!canInviteAny || !effectiveInviteRole) return;
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) {
      setEmailError('Email is required.');
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (members.some((m) => m.user.email.toLowerCase() === trimmed)) {
      setEmailError('This user is already a collaborator on this project.');
      return;
    }
    if (pendingInvitations.some((inv) => inv.email.toLowerCase() === trimmed)) {
      setEmailError('An invitation is already pending for this email.');
      return;
    }
    setEmailError(undefined);

    inviteMutation.mutate(
      { projectUuid, email: trimmed, role: effectiveInviteRole },
      {
        onSuccess: () => {
          setEmail('');
          setRole('editor');
        },
      },
    );
  };

  const requestRemoveMember = (member: ApiProjectMember) => {
    const self = isSelf(member);
    setConfirm({
      title: self ? 'Leave project?' : 'Remove collaborator?',
      message: self
        ? `You will lose access to this project. This cannot be undone.`
        : `${displayName(member.user)} will lose access to this project. This cannot be undone.`,
      confirmLabel: self ? 'Leave' : 'Remove',
      onConfirm: () => {
        removeMutation.mutate({ projectUuid, memberUuid: member.uuid });
        setConfirm(null);
      },
    });
  };

  const requestCancelInvitation = (invitation: ApiProjectInvitation) => {
    setConfirm({
      title: 'Cancel invitation?',
      message: `The invitation sent to ${invitation.email} will be revoked.`,
      confirmLabel: 'Cancel Invitation',
      onConfirm: () => {
        cancelMutation.mutate({ projectUuid, invitationUuid: invitation.uuid });
        setConfirm(null);
      },
    });
  };

  // const requestResendInvitation = (invitation: ApiProjectInvitation) => {
  //   setConfirm({
  //     title: 'Resend invitation?',
  //     message: `An invitation email will be re-sent to ${invitation.email}.`,
  //     confirmLabel: 'Resend',
  //     isDestructive: false,
  //     onConfirm: () => {
  //       resendMutation.mutate({ projectUuid, invitationUuid: invitation.uuid });
  //       setConfirm(null);
  //     },
  //   });
  // };

  const membersError = membersQuery.isError
    ? extractApiError(membersQuery.error, 'Failed to load members')
    : null;
  const invitationsError = invitationsQuery.isError
    ? extractApiError(invitationsQuery.error, 'Failed to load invitations')
    : null;

  return (
    <div className="bg-[#0B0B0B] border border-white/10 rounded-[28px] p-4 sm:p-6 space-y-5">
      <div>
        <h3 className="text-base font-bold text-white">Collaborators & Access</h3>
        <p className="text-zinc-550 text-xs mt-1">
          Manage who has access to this project and their roles.
        </p>
      </div>

      {/* Access summary */}
      <div className="flex flex-wrap items-center gap-2.5 p-3.5 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
        <div className="flex items-center gap-1.5 text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
          <FiShield className="w-3.5 h-3.5" />
          <span>This Project</span>
        </div>
        {(isOwner || selfMember) && (
          <>
            <span className="text-zinc-650">·</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-blue-500/10 text-blue-400 border-blue-500/20">
              Your role:{' '}
              {isOwner
                ? 'Owner'
                : selfMember
                  ? (ROLE_LABEL[selfMember.role] ?? selfMember.role)
                  : ''}
            </span>
          </>
        )}
        <span className="text-zinc-650">·</span>
        <span className="text-zinc-500 text-[10px] font-semibold">
          {memberCount} collaborator{memberCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Invite form — a plain div, not <form>: this card sits inside the
          page's outer <form onSubmit={handleSave}>, and nested HTML forms
          submit unreliably (was reloading the page instead of inviting). */}
      {canInviteAny ? (
        <div className="space-y-3">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
            Invite a Collaborator
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(undefined);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInvite();
                    }
                  }}
                  placeholder="collaborator@email.com"
                  className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 transition-all ${
                    emailError ? 'border-rose-500' : 'border-white/5'
                  }`}
                />
              </div>
              {emailError && (
                <p className="text-rose-500 text-[10px] font-semibold mt-1.5">{emailError}</p>
              )}
            </div>
            <select
              value={effectiveInviteRole}
              onChange={(e) => setRole(e.target.value as ProjectRole)}
              className="bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all cursor-pointer"
            >
              {invitableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleInvite}
              disabled={inviteMutation.isPending}
              className="px-6 py-3.5 rounded-xl bg-white text-black hover:bg-zinc-100 text-xs font-bold transition-all cursor-pointer shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {inviteMutation.isPending ? 'Inviting...' : 'Invite'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-zinc-550 text-[10px] px-3.5 py-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
          You don&apos;t have permission to invite collaborators to this project.
        </p>
      )}

      {/* Pending invitations */}
      <div className="space-y-2.5">
        <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
          Pending Invitations {pendingInvitations.length > 0 && `(${pendingInvitations.length})`}
        </p>

        {invitationsQuery.isLoading ? (
          <InvitationSkeleton />
        ) : invitationsError ? (
          <p className="text-rose-450 text-[10px] font-semibold px-3.5 py-3 bg-rose-500/5 border border-rose-500/10 rounded-2xl">
            {invitationsError}
          </p>
        ) : pendingInvitations.length === 0 ? (
          <p className="text-zinc-550 text-[10px] px-3.5 py-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
            No pending invitations.
          </p>
        ) : (
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.uuid}
                className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
              >
                <div className="min-w-0">
                  <p className="text-white text-xs font-bold truncate">{invitation.email}</p>
                  <p className="text-zinc-550 text-[10px] mt-0.5">
                    {ROLE_LABEL[invitation.role] ?? invitation.role} · Invited{' '}
                    {formatDate(invitation.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/20">
                    {invitation.status}
                  </span>
                  {/* {canInviteRole(invitation.role) &&
                    !invitation.is_automatic &&
                    !invitation.automatic && (
                      <button
                        type="button"
                        title="Resend invitation"
                        disabled={resendMutation.isPending}
                        onClick={() => requestResendInvitation(invitation)}
                        className="p-2 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer disabled:opacity-40"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )} */}
                  {canRemoveRole(invitation.role) && (
                    <button
                      type="button"
                      title="Cancel invitation"
                      onClick={() => requestCancelInvitation(invitation)}
                      className="p-2 rounded-full border border-rose-500/20 hover:border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 transition-all cursor-pointer"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members list */}
      <div className="space-y-2.5">
        <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
          Current Members ({memberCount})
        </p>

        {membersQuery.isLoading ? (
          <MemberSkeleton />
        ) : membersError ? (
          <p className="text-rose-450 text-[10px] font-semibold px-3.5 py-3 bg-rose-500/5 border border-rose-500/10 rounded-2xl">
            {membersError}
          </p>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
            <FiUsers className="w-6 h-6 text-zinc-600" />
            <p className="text-zinc-550 text-xs">No collaborators yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const self = isSelf(member);
              return (
                <div
                  key={member.uuid}
                  className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={displayName(member.user)} size="sm" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white text-xs font-bold truncate">
                          {displayName(member.user)}
                        </span>
                        {self && (
                          <span className="text-zinc-550 text-[9px] font-bold uppercase">You</span>
                        )}
                      </div>
                      <p className="text-zinc-550 text-[10px] mt-0.5 truncate">
                        {member.user.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {canChangeRole && !self ? (
                      <select
                        value={member.role}
                        disabled={updateRoleMutation.isPending}
                        onChange={(e) =>
                          updateRoleMutation.mutate({
                            projectUuid,
                            memberUuid: member.uuid,
                            role: e.target.value as ProjectRole,
                          })
                        }
                        className="bg-zinc-900/60 border border-white/10 text-zinc-300 text-[10px] font-bold uppercase rounded-full px-2.5 py-1 cursor-pointer outline-none"
                      >
                        <option value="editor">Editor</option>
                        <option value="manager">Manager</option>
                        <option value="guest">Guest</option>
                      </select>
                    ) : (
                      <span
                        className="text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border bg-zinc-800 text-zinc-400 border-white/5"
                        title={!self && !canChangeRole ? NO_PERMISSION_TITLE : undefined}
                      >
                        {ROLE_LABEL[member.role] ?? member.role}
                      </span>
                    )}

                    {(self || canRemoveRole(member.role)) && (
                      <button
                        type="button"
                        title={self ? 'Leave project' : 'Remove collaborator'}
                        onClick={() => requestRemoveMember(member)}
                        className="p-2 rounded-full border border-rose-500/20 hover:border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 transition-all cursor-pointer"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      <AnimatePresence>
        {confirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirm(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-sm bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
            >
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-white">{confirm.title}</h2>
                <button
                  onClick={() => setConfirm(null)}
                  className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <p className="text-zinc-400 text-xs mb-6">{confirm.message}</p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirm(null)}
                  className="px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 text-xs font-bold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirm.onConfirm}
                  className={`px-5 py-2.5 rounded-full text-white text-xs font-bold transition-all cursor-pointer ${
                    confirm.isDestructive === false
                      ? 'bg-blue-600 hover:bg-blue-500'
                      : 'bg-rose-500 hover:bg-rose-600'
                  }`}
                >
                  {confirm.confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MemberSkeleton = () => (
  <div className="space-y-2">
    {[0, 1].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
      >
        <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-32 rounded bg-white/5 animate-pulse" />
          <div className="h-2 w-40 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

const InvitationSkeleton = () => (
  <div className="space-y-2">
    {[0].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
      >
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-40 rounded bg-white/5 animate-pulse" />
          <div className="h-2 w-24 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);
